import { useState, useEffect, useContext, Fragment } from 'react'
import styled from '@emotion/styled'
import AddIcon from '@mui/icons-material/Add'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import {
  listItemClasses,
  listItemIconClasses,
  listItemTextClasses,
  ListItem,
  ListItemIcon,
  ListItemText,
  List,
  Typography,
} from '@mui/material'
import AppBar from '@mui/material/AppBar'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { AddFieldDrawer } from '@components/drawers/addField'
import { AddFieldsetDrawer } from '@components/drawers/addFieldsetDrawer'
import { fieldSetContext } from '@contexts'
import { colors } from '@global/constants'
import { PickerBase } from './picker-base'
import { t } from '@lingui/core/macro'

const FieldPickerList = styled(List)({
  paddingTop: '14px',
  paddingBottom: '14px',
  [`& .${listItemClasses.dense}`]: {
    padding: 0,
  },
  [`& .${listItemIconClasses.root}`]: {
    minWidth: 'auto',
  },
  [`& .${listItemTextClasses.root}`]: {
    cursor: 'pointer',
  },
})

export const FieldPicker = props => {
  const [state, setState] = useState({
    selected: {},
    fieldsetIdAddField: null,
    addFieldsVisible: false,
    addFieldsetVisible: false,
    displayFieldsets: [],
    open: {},
  })
  const {
    selected,
    fieldsetIdAddField,
    addFieldsVisible,
    addFieldsetVisible,
    displayFieldsets,
    open,
  } = state
  const { byId, fetchAllFieldSetsAndFields } = useContext(fieldSetContext)

  const filteredFieldsets = filterOutSystemFields(Object.values(byId))

  useEffect(() => {
    if (Object.values(byId).length) return
    fetchAllFieldSetsAndFields()
  }, [])

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      displayFieldsets: filteredFieldsets,
    }))
  }, [byId])

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      selected: {},
    }))
  }, [props.selected])

  useEffect(() => {
    if (fieldsetIdAddField) {
      setState(prevState => ({
        ...prevState,
        addFieldsVisible: true,
      }))
      return
    }

    setState(prevState => ({
      ...prevState,
      addFieldsVisible: false,
    }))
  }, [fieldsetIdAddField])

  function filterOutSystemFields(fields) {
    return fields.filter(
      fs =>
        fs.description !==
        'THIS IS A SYSTEM-GENERATED FIELDSET. PLEASE DO NOT MANUALLY MAKE ANY CHANGES.',
    )
  }

  function search(e) {
    const searchString = e.target.value.toLowerCase()
    if (!searchString) {
      setState(prevState => ({
        ...prevState,
        displayFieldsets: filteredFieldsets,
      }))
    } else {
      setState(prevState => ({
        ...prevState,
        displayFieldsets: filteredFieldsets.reduce((memo, fs) => {
          const filterFields = fs.fields.filter(field =>
            field.name?.toLowerCase().includes(searchString),
          )
          const matchesFieldsetName = fs.name.toLowerCase().includes(searchString)

          if (matchesFieldsetName) {
            const emptyFS = JSON.parse(JSON.stringify(fs))
            memo.push(emptyFS)
          } else if (filterFields.length) {
            const filterFS = JSON.parse(JSON.stringify(fs))
            filterFS.fields = filterFields
            memo.push(filterFS)
          }

          return memo
        }, []),
      }))
    }
  }

  function toggleExpand(item) {
    const newOpen = {
      ...open,
      [item.id]: !open[item.id],
    }
    setState(prevState => ({
      ...prevState,
      open: newOpen,
    }))
  }

  function toggleAllFields(fs, checked) {
    const newSelected = fs.fields.reduce((memo, item) => {
      if (!props.selected[item.id]) {
        memo[item.id] = checked
      }
      return memo
    }, selected)
    setState(prevState => ({
      ...prevState,
      selected: { ...newSelected },
    }))
  }

  function handleChange(field, checked) {
    const newSelected = {
      ...selected,
      [field.id]: !!checked,
    }
    setState(prevState => ({
      ...prevState,
      selected: newSelected,
    }))
  }

  return (
    <PickerBase {...props}>
      <AppBar
        position="relative"
        style={{
          padding: '0 24px',
          flex: 1,
          height: 60,
          maxHeight: 60,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
        }}
      >
        <div style={{ flex: 1 }}>{t`Select fields`}</div>
        <div style={{ flex: 1, justifyContent: 'flex-end', textAlign: 'right' }}>
          <TextField
            placeholder={t`Search`}
            variant="outlined"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.12',
              color: 'white',
              borderRadius: 4,
              margin: 0,
            }}
            onChange={search}
            margin="dense"
            inputProps={{
              style: { color: 'white !important' },
            }}
            InputProps={{
              style: { color: 'white' },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </div>
      </AppBar>
      <div style={{ flex: 1, overflow: 'scroll', padding: '0 4px 0 24px' }}>
        <FieldPickerList>
          {displayFieldsets.map(fs => {
            const labelId = `checkbox-list-label-${fs.id}`
            const fieldIds = fs.fields.map(f => f.id)
            const existingSelectedIds = Object.keys(props.selected).map(s => Number(s))
            const newlySelectedIds = Object.keys(selected)
              .filter(s => selected[s] === true)
              .map(s => Number(s)) // Filter out unchecked (false)
            const someFieldSelected = fieldIds.some(n => existingSelectedIds.includes(n))
            const someNewFieldSelected = fieldIds.some(n => newlySelectedIds.includes(n))
            const allFieldsExistingSelectedIds = fieldIds.every(
              n => existingSelectedIds.includes(n) || newlySelectedIds.includes(n),
            )
            const allFieldsNewlySelectedIds = fieldIds.every(n => newlySelectedIds.includes(n))
            const allFieldsSelectedInFieldset =
              fieldIds.length > 0 && (allFieldsExistingSelectedIds || allFieldsNewlySelectedIds)
            const newlySelectedFieldsets = newlySelectedIds.filter(s =>
              fieldIds.includes(Number(s)),
            )

            const isFieldsetChecked = Boolean(
              allFieldsSelectedInFieldset || newlySelectedFieldsets.length,
            )
            const isFieldChecked = field =>
              selected[field.id] || existingSelectedIds.includes(field.id)
            const isDisabled =
              (allFieldsSelectedInFieldset &&
                !allFieldsNewlySelectedIds &&
                !someNewFieldSelected) ||
              fieldIds.length === 0
            const isIndeterminate =
              !allFieldsSelectedInFieldset &&
              fieldIds.length > 0 &&
              (newlySelectedFieldsets.length > 0 || someFieldSelected)

            return (
              <Fragment key={fs.id}>
                <ListItem role={undefined} dense>
                  <ListItemIcon>
                    <div>
                      <Checkbox
                        edge="start"
                        checked={isFieldsetChecked}
                        disabled={isDisabled}
                        indeterminate={isIndeterminate}
                        tabIndex={-1}
                        disableRipple
                        onChange={(e, val) => {
                          toggleAllFields(fs, val)
                        }}
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                      <Tooltip title={t`Add new field`}>
                        <Button
                          size="medium"
                          variant="outlined"
                          color="secondary"
                          onClick={e => {
                            e.preventDefault()
                            setState(prevState => ({
                              ...prevState,
                              fieldsetIdAddField: fs.id,
                            }))
                          }}
                          style={{
                            marginRight: '10px',
                            minWidth: 0,
                            padding: 0,
                            borderRadius: '10px',
                            width: '21px',
                            height: '21px',
                          }}
                        >
                          <AddIcon />
                        </Button>
                      </Tooltip>
                    </div>
                  </ListItemIcon>
                  <ListItemText
                    disableTypography
                    id={labelId}
                    primary={
                      <Typography
                        style={{ fontSize: '12px', color: isDisabled ? '#9C9C9C' : '#222' }}
                      >
                        {fs.name}
                      </Typography>
                    }
                    onClick={() => {
                      toggleAllFields(fs, !isFieldsetChecked)
                    }}
                  />
                  {fs.fields.length > 0 && (
                    <ListItemSecondaryAction>
                      <IconButton
                        name={`expand-fieldset-${fs.id}`}
                        edge="end"
                        aria-label="comments"
                        onClick={() => {
                          toggleExpand(fs)
                        }}
                        size="large"
                      >
                        {open[fs.id] ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
                <Collapse in={open[fs.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {fs.fields.map(field => (
                      <ListItem
                        data-test-fieldid={field.id}
                        style={{ paddingLeft: 24 }}
                        dense
                        key={field.id}
                      >
                        <ListItemIcon>
                          <Checkbox
                            name={`checkbox-${field.id}`}
                            edge="start"
                            checked={isFieldChecked(field)}
                            disabled={Boolean(props.selected[field.id])}
                            disableRipple
                            onChange={(e, val) => {
                              handleChange(field, val)
                            }}
                            inputProps={{ 'aria-labelledby': labelId }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              style={{
                                fontSize: '12px',
                                color: props.selected[field.id] ? '#9C9C9C' : '#222',
                              }}
                            >
                              {field.name}
                            </Typography>
                          }
                          onClick={() => handleChange(field, !isFieldChecked(field))}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Fragment>
            )
          })}
        </FieldPickerList>
        {!displayFieldsets.length ? (
          <Typography variant="subtitle2" style={{ textAlign: 'center', marginTop: 24 }}>
            {t`No matching fields`}
          </Typography>
        ) : null}
      </div>
      <div
        style={{
          flex: 1,
          padding: '0 24px',
          height: 60,
          maxHeight: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid #DDE1E3',
        }}
      >
        <Button
          startIcon={<AddIcon />}
          size="medium"
          variant="outlined"
          color="secondary"
          onClick={e => {
            e.preventDefault()
            setState(prevState => ({
              ...prevState,
              addFieldsetVisible: true,
            }))
          }}
        >
          {t`Add field set`}
        </Button>
        <Button
          name="select-fields"
          variant="contained"
          color="secondary"
          onClick={() => {
            props.onApplySelection(Object.keys(selected).filter(k => selected[k]))
            setState(prevState => ({
              ...prevState,
              displayFieldsets: Object.values(byId),
            }))
          }}
          sx={{
            padding: '6px 28px',
            color: colors.white,
          }}
        >
          {t`Select fields`}
        </Button>
        <AddFieldDrawer
          isOpen={addFieldsVisible}
          onDrawerClose={() => {
            setState(prevState => ({
              ...prevState,
              fieldsetIdAddField: null,
            }))
          }}
          fieldset={byId[fieldsetIdAddField]}
        />
        <AddFieldsetDrawer
          isOpen={addFieldsetVisible}
          onDrawerClose={() => {
            setState(prevState => ({
              ...prevState,
              addFieldsetVisible: false,
            }))
          }}
          onSubmit={() => {
            setState(prevState => ({
              ...prevState,
              addFieldsetVisible: false,
            }))
          }}
        />
      </div>
    </PickerBase>
  )
}
